import { useEffect, useMemo, useRef } from 'react';

import { OrbitControls } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

export interface IesGrid {
  /** azimuth angles in radians, ascending in [0, 2π) */
  azimuth: number[];
  /** elevation angles in radians, ascending; 0 = nadir (straight down) */
  elevation: number[];
  /** candela[azimuthIndex][elevationIndex] in cd */
  candela: number[][];
}

// Coordinate convention matches the new-project CAD flow: Z-up.
// Ground = XY plane (z = 0), height = +Z, distribution nadir points down (−Z).

// ── Geometry: candela "balloon" built directly from the grid ────────────────
// Returns a geometry centred on the emission point (origin), scaled so the peak
// lobe reaches `targetRadius`. Vertex colour fades dim core → bright lobe.
const buildDistributionGeometry = (grid: IesGrid, color: string, targetRadius: number): THREE.BufferGeometry => {
  const { azimuth, elevation, candela } = grid;
  const nAz = azimuth.length;
  const nEl = elevation.length;

  let maxC = 1e-6;
  for (let a = 0; a < nAz; a++) {
    for (let e = 0; e < nEl; e++) {
      const c = candela[a]?.[e] ?? 0;
      if (c > maxC) maxC = c;
    }
  }
  const scale = targetRadius / maxC;
  const base = new THREE.Color(color);

  const positions = new Float32Array(nEl * nAz * 3);
  const colors = new Float32Array(nEl * nAz * 3);
  let p = 0;
  for (let e = 0; e < nEl; e++) {
    const ah = elevation[e]!;
    for (let a = 0; a < nAz; a++) {
      const av = azimuth[a]!;
      const cRaw = candela[a]?.[e] ?? 0;
      const c = cRaw * scale;
      positions[p * 3] = c * Math.sin(ah) * Math.cos(av);
      positions[p * 3 + 1] = c * Math.sin(ah) * Math.sin(av);
      positions[p * 3 + 2] = -c * Math.cos(ah); // nadir → −Z (down)
      const t = cRaw / maxC;
      const k = 0.25 + 0.75 * t;
      colors[p * 3] = base.r * k;
      colors[p * 3 + 1] = base.g * k;
      colors[p * 3 + 2] = base.b * k;
      p++;
    }
  }

  const index: number[] = [];
  const idx = (e: number, a: number) => e * nAz + (a % nAz);
  for (let e = 0; e < nEl - 1; e++) {
    for (let a = 0; a < nAz; a++) {
      const an = (a + 1) % nAz;
      index.push(idx(e, a), idx(e + 1, a), idx(e, an), idx(e + 1, a), idx(e + 1, an), idx(e, an));
    }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  g.setIndex(index);
  g.computeVertexNormals();
  return g;
};

// Largest index i with arr[i] <= x (arr sorted ascending), else 0.
const lowerIndex = (arr: number[], x: number): number => {
  let lo = 0;
  let hi = arr.length - 1;
  let res = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid]! <= x) {
      res = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return res;
};

// Bilinear sample of candela at (theta = elevation from nadir, az in [0,2π)).
const sampleCandela = (grid: IesGrid, theta: number, az: number): number => {
  const { azimuth, elevation, candela } = grid;
  const nAz = azimuth.length;
  const nEl = elevation.length;
  if (nEl === 0 || nAz === 0) return 0;

  const e1 = lowerIndex(elevation, theta);
  const e2 = Math.min(e1 + 1, nEl - 1);
  const de = elevation[e2]! - elevation[e1]!;
  const te = de > 0 ? Math.min(1, Math.max(0, (theta - elevation[e1]!) / de)) : 0;

  let a1 = lowerIndex(azimuth, az);
  if (azimuth[a1]! > az) a1 = nAz - 1; // az below first sample → wrap from top
  const a2 = (a1 + 1) % nAz;
  const az1 = azimuth[a1]!;
  let az2 = azimuth[a2]!;
  let azq = az;
  if (a2 <= a1) {
    az2 += 2 * Math.PI;
    if (azq < az1) azq += 2 * Math.PI;
  }
  const da = az2 - az1;
  const ta = da > 0 ? Math.min(1, Math.max(0, (azq - az1) / da)) : 0;

  const c = (a: number, e: number) => candela[a]?.[e] ?? 0;
  const top = c(a1, e1) * (1 - ta) + c(a2, e1) * ta;
  const bot = c(a1, e2) * (1 - ta) + c(a2, e2) * ta;
  return top * (1 - te) + bot * te;
};

// Build "nice" iso-contour levels adapted to the actual peak illuminance.
// Picks a 1/2/5 × 10ⁿ step so we get roughly 6-10 levels spanning (0, maxLux).
const buildDynamicLevels = (maxLux: number): number[] => {
  if (!Number.isFinite(maxLux) || maxLux <= 0) return [];
  const target = 8;
  const rawStep = maxLux / target;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  let step = mag;
  for (const m of [1, 2, 5, 10]) {
    const s = m * mag;
    if (maxLux / s <= target + 1) {
      step = s;
      break;
    }
  }
  const levels: number[] = [];
  for (let k = 1; k * step < maxLux * 0.985; k++) {
    const v = k * step;
    if (v > maxLux * 0.005) levels.push(parseFloat(v.toPrecision(3)));
  }
  return levels;
};

// ── Ground illuminance footprint: gradient + isolux contour lines (lx) ──────
const buildFootprintTexture = (grid: IesGrid, mountingHeight: number, groundSize: number, color: string) => {
  const N = 4096;
  const canvas = document.createElement('canvas');
  canvas.width = N;
  canvas.height = N;
  const ctx = canvas.getContext('2d')!;

  const { elevation } = grid;
  const elMin = elevation[0] ?? 0;
  const elMax = elevation[elevation.length - 1] ?? Math.PI;
  const H = mountingHeight;

  // Illuminance in lux across the ground (fixture at XY origin).
  const lux = new Float32Array(N * N);
  let maxLux = 1e-9;
  for (let j = 0; j < N; j++) {
    const wy = -groundSize / 2 + (j / (N - 1)) * groundSize;
    for (let i = 0; i < N; i++) {
      const wx = -groundSize / 2 + (i / (N - 1)) * groundSize;
      const r = Math.hypot(wx, wy);
      const d = Math.hypot(r, H);
      const theta = Math.acos(Math.min(1, H / d));
      let az = Math.atan2(wy, wx);
      if (az < 0) az += 2 * Math.PI;
      let val = 0;
      if (theta >= elMin && theta <= elMax) {
        const I = sampleCandela(grid, theta, az);
        // E = I * cos(θ) / d² ; cos(θ) = H/d → E = I·H / d³  (lux, with I in cd, d in metres)
        val = (I * H) / (d * d * d);
      }
      lux[j * N + i] = val;
      if (val > maxLux) maxLux = val;
    }
  }

  // Base gradient fill.
  const base = new THREE.Color(color);
  const floorGrey = 12;
  const img = ctx.createImageData(N, N);
  for (let k = 0; k < N * N; k++) {
    const t = Math.pow(lux[k]! / maxLux, 0.5);
    img.data[k * 4] = Math.min(255, floorGrey + base.r * 255 * t);
    img.data[k * 4 + 1] = Math.min(255, floorGrey + base.g * 255 * t);
    img.data[k * 4 + 2] = Math.min(255, floorGrey + base.b * 255 * t);
    img.data[k * 4 + 3] = 255;
  }

  // Isolux contour lines — mark where the field crosses a chosen level.
  // At 2048² we draw a thin (~2px) line by also marking the neighbour pixel.
  const levels = buildDynamicLevels(maxLux);
  const crosses = (v1: number, v2: number, lvl: number) => (v1 < lvl && v2 >= lvl) || (v2 < lvl && v1 >= lvl);
  const line = new Uint8Array(N * N);
  // For each level, remember a representative pixel where the contour was first hit
  // (scanning bottom-up, so we tend to land on the +Y arc of each lobe — close to
  // the camera-friendly far side of the contour).
  const labelPositions = new Map<number, { i: number; j: number }>();
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const v = lux[j * N + i]!;
      if (i < N - 1) {
        const vr = lux[j * N + i + 1]!;
        for (const lvl of levels) {
          if (crosses(v, vr, lvl)) {
            line[j * N + i] = 1;
            line[j * N + i + 1] = 1;
            if (!labelPositions.has(lvl)) labelPositions.set(lvl, { i, j });
            break;
          }
        }
      }
      if (j < N - 1) {
        const vd = lux[(j + 1) * N + i]!;
        for (const lvl of levels) {
          if (crosses(v, vd, lvl)) {
            line[j * N + i] = 1;
            line[(j + 1) * N + i] = 1;
            if (!labelPositions.has(lvl)) labelPositions.set(lvl, { i, j });
            break;
          }
        }
      }
    }
  }
  for (let k = 0; k < N * N; k++) {
    if (line[k]) {
      img.data[k * 4] = 240;
      img.data[k * 4 + 1] = 240;
      img.data[k * 4 + 2] = 240;
    }
  }
  ctx.putImageData(img, 0, 0);

  // Draw level labels (in lx) on top of the gradient + lines.
  if (labelPositions.size > 0) {
    const fontPx = Math.round(N / 64); // ~32px at N=2048
    ctx.font = `600 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(2, Math.round(fontPx / 4));
    const fmt = (v: number) => {
      if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
      if (v >= 10) return v.toFixed(0);
      if (v >= 1) return v.toFixed(1);
      if (v >= 0.1) return v.toFixed(2);
      return v.toPrecision(2);
    };
    for (const [lvl, pos] of labelPositions) {
      const text = `${fmt(lvl)} lx`;
      // The canvas X-axis maps opposite to the texture U-axis as seen from the camera,
      // so we apply a horizontal flip around the label centre to un-mirror the glyphs.
      ctx.save();
      ctx.translate(pos.i, pos.j);
      ctx.scale(-1, 1);
      ctx.strokeStyle = 'rgba(0,0,0,0.85)';
      ctx.strokeText(text, 0, 0);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.flipY = false;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return { tex, maxLux };
};

const DistributionMesh = ({
  grid,
  color,
  radius,
  wireframe = false,
}: {
  grid: IesGrid;
  color: string;
  radius: number;
  wireframe?: boolean;
}) => {
  const geometry = useMemo(() => buildDistributionGeometry(grid, color, radius), [grid, color, radius]);
  if (wireframe) {
    return (
      <mesh geometry={geometry}>
        <meshBasicMaterial color={color} wireframe transparent opacity={0.6} toneMapped={false} />
      </mesh>
    );
  }
  return (
    <mesh geometry={geometry}>
      <meshBasicMaterial
        vertexColors
        transparent
        opacity={0.32}
        side={THREE.DoubleSide}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </mesh>
  );
};

const Ground = ({
  grid,
  color,
  mountingHeight,
  size,
}: {
  grid: IesGrid;
  color: string;
  mountingHeight: number;
  size: number;
}) => {
  const { tex } = useMemo(
    () => buildFootprintTexture(grid, mountingHeight, size, color),
    [grid, color, mountingHeight, size],
  );
  return (
    <mesh position={[0, 0, 0]}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial map={tex} toneMapped={false} side={THREE.DoubleSide} />
    </mesh>
  );
};

// Resets camera & OrbitControls to the default position for the given dist/H
// whenever either value changes, without remounting the Canvas.
const CameraReset = ({ dist, H }: { dist: number; H: number }) => {
  const { camera } = useThree();
  const controlsRef = useRef<OrbitControlsImpl>(null);

  useEffect(() => {
    camera.position.set(dist * 0.7, -dist * 0.7, dist * 0.6);
    camera.up.set(0, 0, 1);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    if (controlsRef.current) {
      controlsRef.current.update();
      controlsRef.current.saveState();
      controlsRef.current.reset();
    }
  }, [dist, H, camera]);

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enablePan={false}
      minDistance={H * 1.5}
      maxDistance={dist * 1.8}
      maxPolarAngle={Math.PI / 2 - 0.04}
      target={[0, 0, 0]}
    />
  );
};

// Calls onFirstRender after the first frame is rendered inside the Canvas.
const FirstRenderNotifier = ({ onFirstRender }: { onFirstRender: () => void }) => {
  useFrame(() => onFirstRender());
  return null;
};

interface IesSceneProps {
  grid: IesGrid;
  color: string;
  /** mounting height in metres */
  mountingHeight?: number;
  /** Render the candela distribution as a wireframe instead of the shaded balloon. */
  wireframe?: boolean;
  /** Ground plane edge length as a multiple of mounting height. Default 4.5. */
  groundScale?: number;
  /** Called once the first frame has been rendered. */
  onFirstRender?: () => void;
}

export const IesScene = ({
  grid,
  color,
  mountingHeight = 7,
  wireframe = false,
  groundScale = 4.5,
  onFirstRender,
}: IesSceneProps) => {
  const H = mountingHeight;
  const ground = H * groundScale;
  const fixture: [number, number, number] = [0, 0, H];
  const balloonRadius = H * 0.42;
  const dist = ground * (groundScale >= 8 ? 0.7 : 1.15);
  const firedRef = useRef(false);

  // Reset so onFirstRender fires again after each height change
  useEffect(() => {
    firedRef.current = false;
  }, [H]);

  return (
    <Canvas
      // Z-up, isometric-ish view matching the new-project CAD flow.
      camera={{ position: [dist * 0.7, -dist * 0.7, dist * 0.6], fov: 38, near: 0.1, far: 5000 }}
      gl={{ antialias: true, alpha: false }}
      style={{ width: '100%', height: '100%' }}
      onCreated={({ scene, camera }) => {
        scene.background = new THREE.Color('#0a0a0a');
        camera.up.set(0, 0, 1); // Z-up
        camera.lookAt(0, 0, 0);
      }}
      onPointerMissed={undefined}
    >
      <ambientLight intensity={0.4} />
      <hemisphereLight args={['#3a3a3a', '#0c0c0c', 0.45]} />
      <directionalLight position={[ground * 0.5, -ground * 0.5, ground]} intensity={0.4} />
      <pointLight position={fixture} color={color} intensity={3} distance={ground} decay={2} />

      <Ground grid={grid} color={color} mountingHeight={H} size={ground} />

      {/* Luminaire housing */}
      <mesh position={fixture}>
        <boxGeometry args={[0.55, 0.32, 0.16]} />
        <meshStandardMaterial color="#1f1f1f" roughness={0.5} metalness={0.5} />
      </mesh>

      <group position={fixture}>
        <DistributionMesh grid={grid} color={color} radius={balloonRadius} wireframe={wireframe} />
      </group>

      <CameraReset dist={dist} H={H} />
      {onFirstRender && (
        <FirstRenderNotifier
          onFirstRender={() => {
            if (!firedRef.current) {
              firedRef.current = true;
              onFirstRender();
            }
          }}
        />
      )}
    </Canvas>
  );
};

// ── Standalone LDC (light distribution curve) wireframe ─────────────────────
// User-controlled rotation (no auto-spin). Z-up to match the main scene.
const LdcMesh = ({ grid, color }: { grid: IesGrid; color: string }) => {
  const geometry = useMemo(() => buildDistributionGeometry(grid, color, 1), [grid, color]);
  return (
    <mesh geometry={geometry} position={[0, 0, 0.55]}>
      <meshBasicMaterial color={color} wireframe transparent opacity={0.55} toneMapped={false} />
    </mesh>
  );
};

export const LdcWireframe = ({ grid, color }: { grid: IesGrid; color: string }) => (
  <Canvas
    camera={{ position: [2.4, -2.4, 1.2], fov: 42 }}
    gl={{ alpha: true }}
    style={{ width: '100%', height: '100%' }}
    onCreated={({ camera }) => {
      camera.up.set(0, 0, 1);
      camera.lookAt(0, 0, 0);
    }}
  >
    <LdcMesh grid={grid} color={color} />
    <OrbitControls makeDefault enablePan={false} enableZoom enableDamping target={[0, 0, 0]} />
  </Canvas>
);
